import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Search, 
  BookOpen, 
  Users, 
  Star, 
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Archive,
  LayoutGrid,
  List,
  Clock,
  CheckCircle,
  AlertCircle,
  Send,
  Filter,
  FolderOpen,
  Calendar,
  AlertTriangle,
  FileText,
  Radio,
  Headphones,
  Bell,
  DollarSign
} from 'lucide-react';
import { coursesApi, CourseDeletionCheckResponse } from '../../lib/api';
import PremiumDropdown, { DropdownOption } from '../../components/ui/PremiumDropdown';
import { formatPrice } from '../../lib/utils';
import toast from 'react-hot-toast';
import { InlineLoader } from '../../components/ui/PremiumLoader';

interface Course {
  id: string;
  title: string;
  slug: string;
  thumbnailUrl?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  level: string;
  price: number;
  totalStudents: number;
  totalLessons: number;
  rating?: number;
  totalReviews: number;
  createdAt: string;
  updatedAt: string;
  category?: {
    id: string;
    name: string;
  };
}

type ViewMode = 'grid' | 'list';
type StatusFilter = 'ALL' | 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export default function ProfessorCoursesPage() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  
  // Course deletion modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletionCheckData, setDeletionCheckData] = useState<CourseDeletionCheckResponse | null>(null);
  const [isCheckingDeletion, setIsCheckingDeletion] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState<string | null>(null);
  const [isSendingNotification, setIsSendingNotification] = useState(false);
  const [notificationSent, setNotificationSent] = useState(false);

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await coursesApi.getMyCourses();
      if (res.data.success) {
        setCourses(res.data.data as Course[]);
      }
    } catch (error) {
      console.error('Failed to fetch courses:', error);
      toast.error('Failed to load courses');
    } finally {
      setIsLoading(false);
    }
  };

  // Check if course can be deleted before showing modal
  const handleDeleteCheck = async (courseId: string) => {
    setIsCheckingDeletion(true);
    setCourseToDelete(courseId);
    setOpenMenuId(null);
    
    try {
      const res = await coursesApi.checkDeletion(courseId);
      if (res.data.success && res.data.data) {
        setDeletionCheckData(res.data.data);
        setShowDeleteModal(true);
      }
    } catch {
      toast.error('Failed to check course status');
    } finally {
      setIsCheckingDeletion(false);
    }
  };

  // Perform the actual deletion
  const handleDelete = async (force: boolean = false) => {
    if (!courseToDelete) return;
    
    setIsDeleting(courseToDelete);
    try {
      await coursesApi.delete(courseToDelete, force);
      toast.success('Course archived successfully');
      setShowDeleteModal(false);
      setDeletionCheckData(null);
      setCourseToDelete(null);
      fetchCourses();
    } catch (error) {
      console.error('Failed to delete course:', error);
      toast.error('Failed to archive course');
    } finally {
      setIsDeleting(null);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletionCheckData(null);
    setCourseToDelete(null);
    setNotificationSent(false);
  };

  // Send unenroll notification to students
  const handleNotifyStudents = async () => {
    if (!courseToDelete) return;
    
    setIsSendingNotification(true);
    try {
      const res = await coursesApi.notifyUnenroll(courseToDelete);
      if (res.data.success) {
        toast.success(res.data.data?.message || 'Notifications sent to students');
        setNotificationSent(true);
      }
    } catch {
      toast.error('Failed to send notifications');
    } finally {
      setIsSendingNotification(false);
    }
  };

  const handlePublish = async (courseId: string) => {
    try {
      await coursesApi.update(courseId, { status: 'PUBLISHED' });
      toast.success('Course published successfully!');
      fetchCourses();
    } catch (error) {
      console.error('Failed to publish course:', error);
      toast.error('Failed to publish course');
    } finally {
      setOpenMenuId(null);
    }
  };

  const filteredCourses = courses.filter((course) => {
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'ALL' || course.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: courses.length,
    published: courses.filter(c => c.status === 'PUBLISHED').length,
    draft: courses.filter(c => c.status === 'DRAFT').length,
    totalStudents: courses.reduce((acc, c) => acc + c.totalStudents, 0),
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'PUBLISHED':
        return { 
          icon: CheckCircle, 
          color: 'text-emerald-400', 
          bg: 'bg-emerald-500/20',
          border: 'border-emerald-500/30',
          label: 'Published' 
        };
      case 'DRAFT':
        return { 
          icon: Clock, 
          color: 'text-amber-400', 
          bg: 'bg-amber-500/20',
          border: 'border-amber-500/30',
          label: 'Draft' 
        };
      case 'ARCHIVED':
        return { 
          icon: Archive, 
          color: 'text-white/40', 
          bg: 'bg-white/10',
          border: 'border-white/20',
          label: 'Archived' 
        };
      default:
        return { 
          icon: AlertCircle, 
          color: 'text-white/40', 
          bg: 'bg-white/10',
          border: 'border-white/20',
          label: status 
        };
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="skeleton h-20 rounded-2xl" />
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-64 rounded-2xl" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8"
      >
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white">My Courses</h1>
          <p className="text-white/50 mt-1">Manage and organize your course library</p>
        </div>
        <Link
          to="/professor/courses/new"
          className="btn-primary inline-flex items-center gap-2 w-fit"
        >
          <Plus className="w-5 h-5" />
          Create New Course
        </Link>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8"
      >
        {[
          { label: 'Total Courses', value: stats.total, icon: BookOpen, color: 'text-blue-400' },
          { label: 'Published', value: stats.published, icon: CheckCircle, color: 'text-emerald-400' },
          { label: 'Drafts', value: stats.draft, icon: Clock, color: 'text-amber-400' },
          { label: 'Total Students', value: stats.totalStudents, icon: Users, color: 'text-purple-400' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="p-4 sm:p-5 rounded-xl transition-all duration-300"
            style={{
              background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(15, 16, 20, 0.95) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.06)'
            }}
          >
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0 ${stat.color}`}>
                <stat.icon className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div className="min-w-0">
                <div className="text-xl sm:text-2xl font-bold text-white">{stat.value}</div>
                <div className="text-xs sm:text-sm text-white/50 truncate">{stat.label}</div>
              </div>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mb-6"
      >
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search courses..."
            className="input pl-12 w-full"
          />
        </div>

        {/* Status Filter */}
        <PremiumDropdown
          label="Status"
          icon={<Filter className="w-4 h-4" />}
          options={[
            { value: 'ALL', label: 'All Status' },
            { value: 'PUBLISHED', label: 'Published' },
            { value: 'DRAFT', label: 'Draft' },
            { value: 'ARCHIVED', label: 'Archived' },
          ] as DropdownOption[]}
          value={statusFilter}
          onChange={(value) => setStatusFilter(value as StatusFilter)}
          placeholder="All Status"
        />

        {/* View Toggle */}
        <div className="flex items-center gap-1 p-1 rounded-lg bg-white/5">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-2.5 rounded-md transition-colors ${
              viewMode === 'grid' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-2.5 rounded-md transition-colors ${
              viewMode === 'list' ? 'bg-white/10 text-white' : 'text-white/40 hover:text-white'
            }`}
          >
            <List className="w-4 h-4" />
          </button>
        </div>
      </motion.div>

      {/* Courses Grid/List */}
      <AnimatePresence mode="wait">
        {filteredCourses.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-6">
              <BookOpen className="w-10 h-10 text-white/30" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">No courses found</h3>
            <p className="text-white/50 mb-6">
              {searchQuery || statusFilter !== 'ALL' 
                ? 'Try adjusting your search or filters' 
                : "You haven't created any courses yet"}
            </p>
            {!searchQuery && statusFilter === 'ALL' && (
              <Link to="/professor/courses/new" className="btn-primary inline-flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Create Your First Course
              </Link>
            )}
          </motion.div>
        ) : viewMode === 'grid' ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6"
          >
            {filteredCourses.map((course, index) => {
              const statusConfig = getStatusConfig(course.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group relative rounded-2xl overflow-hidden transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.03) 0%, rgba(15, 16, 20, 0.95) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-white/5">
                    {course.thumbnailUrl ? (
                      <img
                        src={course.thumbnailUrl}
                        alt={course.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <BookOpen className="w-12 h-12 text-white/20" />
                      </div>
                    )}
                    
                    {/* Status Badge */}
                    <div className={`absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}>
                      <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                      <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                    </div>

                    {/* Menu Button */}
                    <div className="absolute top-3 right-3">
                      <button
                        onClick={() => setOpenMenuId(openMenuId === course.id ? null : course.id)}
                        className="p-2 rounded-lg bg-black/50 backdrop-blur-sm text-white/70 hover:text-white transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>
                      
                      {/* Dropdown Menu */}
                      <AnimatePresence>
                        {openMenuId === course.id && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: -10 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: -10 }}
                            className="absolute right-0 top-12 w-48 rounded-xl overflow-hidden z-10"
                            style={{
                              background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
                            }}
                          >
                            <Link
                              to={`/professor/courses/${course.id}/edit`}
                              className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                            >
                              <Edit className="w-4 h-4" />
                              <span>Edit Course</span>
                            </Link>
                            <Link
                              to={`/professor/courses/${course.id}/documents`}
                              className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                            >
                              <FolderOpen className="w-4 h-4" />
                              <span>Documents</span>
                            </Link>
                            <Link
                              to={`/courses/${course.slug}`}
                              className="flex items-center gap-3 px-4 py-3 text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                            >
                              <Eye className="w-4 h-4" />
                              <span>View Public</span>
                            </Link>
                            {course.status === 'DRAFT' && (
                              <button
                                onClick={() => handlePublish(course.id)}
                                className="flex items-center gap-3 px-4 py-3 w-full text-emerald-400 hover:bg-emerald-500/10 transition-colors"
                              >
                                <Send className="w-4 h-4" />
                                <span>Publish</span>
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteCheck(course.id)}
                              disabled={isCheckingDeletion}
                              className="flex items-center gap-3 px-4 py-3 w-full text-coral hover:bg-coral/10 transition-colors disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>{isCheckingDeletion && courseToDelete === course.id ? 'Checking...' : 'Archive'}</span>
                            </button>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <Link
                        to={`/professor/courses/${course.id}/edit`}
                        className="font-semibold text-white group-hover:text-coral transition-colors line-clamp-2"
                      >
                        {course.title}
                      </Link>
                    </div>

                    {course.category && (
                      <span className="inline-block px-2 py-1 text-xs font-medium text-white/50 bg-white/5 rounded-md mb-3">
                        {course.category.name}
                      </span>
                    )}

                    <div className="flex items-center gap-4 text-sm text-white/50">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {course.totalStudents}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.totalLessons} lessons
                      </span>
                      {course.rating && (
                        <span className="flex items-center gap-1">
                          <Star className="w-4 h-4 text-amber-400" />
                          {course.rating.toFixed(1)}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between">
                      <span className="font-semibold text-white">
                        {course.price === 0 ? 'Free' : formatPrice(course.price)}
                      </span>
                      <Link
                        to={`/professor/courses/${course.id}/edit`}
                        className="text-sm font-medium text-coral hover:text-coral/80 transition-colors"
                      >
                        Edit →
                      </Link>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {filteredCourses.map((course, index) => {
              const statusConfig = getStatusConfig(course.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className="group flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 p-4 rounded-xl transition-all duration-300 hover:bg-white/[0.03]"
                  style={{
                    background: 'linear-gradient(145deg, rgba(255, 255, 255, 0.02) 0%, rgba(15, 16, 20, 0.95) 100%)',
                    border: '1px solid rgba(255, 255, 255, 0.06)'
                  }}
                >
                  {/* Thumbnail and basic info row on mobile */}
                  <div className="flex items-center gap-3 sm:contents">
                    {/* Thumbnail */}
                    <div className="w-16 h-12 sm:w-24 sm:h-16 rounded-lg overflow-hidden bg-white/5 flex-shrink-0">
                      {course.thumbnailUrl ? (
                        <img
                          src={course.thumbnailUrl}
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-white/20" />
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <Link
                        to={`/professor/courses/${course.id}/edit`}
                        className="font-semibold text-sm sm:text-base text-white group-hover:text-coral transition-colors line-clamp-1"
                      >
                        {course.title}
                      </Link>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-1 text-xs sm:text-sm text-white/40">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          {course.totalStudents}
                        </span>
                        <span className="flex items-center gap-1">
                          <BookOpen className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                          {course.totalLessons}
                        </span>
                        {course.rating && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-400" />
                            {course.rating.toFixed(1)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Mobile: Status badge inline */}
                    <div className={`sm:hidden flex items-center gap-1 px-2 py-1 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}>
                      <StatusIcon className={`w-3 h-3 ${statusConfig.color}`} />
                    </div>
                  </div>

                  {/* Desktop: Status */}
                  <div className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full ${statusConfig.bg} ${statusConfig.border} border`}>
                    <StatusIcon className={`w-3.5 h-3.5 ${statusConfig.color}`} />
                    <span className={`text-xs font-medium ${statusConfig.color}`}>{statusConfig.label}</span>
                  </div>

                  {/* Price */}
                  <div className="hidden sm:block w-20 text-right">
                    <span className="font-semibold text-white">
                      {course.price === 0 ? 'Free' : formatPrice(course.price)}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between sm:justify-end gap-2 pt-2 sm:pt-0 border-t sm:border-t-0 border-white/5">
                    <span className="sm:hidden font-semibold text-white text-sm">
                      {course.price === 0 ? 'Free' : formatPrice(course.price)}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Link
                        to={`/professor/courses/${course.id}/edit`}
                        className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                        title="Edit Course"
                      >
                        <Edit className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/professor/courses/${course.id}/documents`}
                        className="hidden sm:block p-2 rounded-lg text-white/40 hover:text-purple-400 hover:bg-purple-500/10 transition-colors"
                        title="Documents"
                      >
                        <FolderOpen className="w-4 h-4" />
                      </Link>
                      <Link
                        to={`/courses/${course.slug}`}
                        className="p-2 rounded-lg text-white/40 hover:text-white hover:bg-white/10 transition-colors"
                        title="View Public"
                      >
                        <Eye className="w-4 h-4" />
                      </Link>
                      <button
                        onClick={() => handleDeleteCheck(course.id)}
                        disabled={isCheckingDeletion}
                        className="p-2 rounded-lg text-white/40 hover:text-coral hover:bg-coral/10 transition-colors disabled:opacity-50"
                        title="Archive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Click outside to close menu */}
      {openMenuId && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setOpenMenuId(null)}
        />
      )}

      {/* Course Deletion Modal */}
      {showDeleteModal && deletionCheckData && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{
              background: 'linear-gradient(145deg, rgba(30, 30, 35, 0.98) 0%, rgba(20, 20, 25, 0.98) 100%)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              boxShadow: '0 25px 50px rgba(0, 0, 0, 0.5)'
            }}
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-6">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  deletionCheckData.canDelete ? 'bg-amber-500/10' : 
                  deletionCheckData.requiresSupport ? 'bg-purple-500/10' : 'bg-red-500/10'
                }`}>
                  {deletionCheckData.canDelete ? (
                    <Archive className="w-6 h-6 text-amber-400" />
                  ) : deletionCheckData.requiresSupport ? (
                    <Headphones className="w-6 h-6 text-purple-400" />
                  ) : (
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">
                    {deletionCheckData.canDelete ? 'Archive Course?' : 
                     deletionCheckData.requiresSupport ? 'Contact Support Required' : 
                     'Cannot Archive Course'}
                  </h3>
                  <p className="text-white/50 text-sm truncate max-w-xs">
                    {deletionCheckData.courseTitle}
                  </p>
                </div>
              </div>

              {/* Blocked Reason */}
              {!deletionCheckData.canDelete && deletionCheckData.blockedReason && (
                <div className={`rounded-xl p-4 mb-6 ${
                  deletionCheckData.requiresSupport 
                    ? 'bg-purple-500/5 border border-purple-500/20' 
                    : 'bg-amber-500/5 border border-amber-500/20'
                }`}>
                  <p className="text-white/80 text-sm">
                    {deletionCheckData.blockedReason}
                  </p>
                </div>
              )}

              {/* Warnings / Info Cards */}
              <div className="space-y-3 mb-6">
                {/* Active Enrollments */}
                {deletionCheckData.hasActiveEnrollments && (
                  <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-400 text-sm">Active Students</h4>
                        <p className="text-white/60 text-sm mt-1">
                          {deletionCheckData.activeEnrollmentsCount} student{deletionCheckData.activeEnrollmentsCount !== 1 ? 's are' : ' is'} enrolled in this course.
                        </p>
                        {deletionCheckData.enrolledStudents.length > 0 && (
                          <div className="flex items-center gap-1 mt-2">
                            {deletionCheckData.enrolledStudents.slice(0, 5).map((student, idx) => (
                              <div 
                                key={student.id}
                                className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-[10px] font-medium text-white/70 -ml-1 first:ml-0"
                                style={{ zIndex: 5 - idx }}
                                title={student.studentName}
                              >
                                {student.avatarUrl ? (
                                  <img src={student.avatarUrl} alt="" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  student.studentName.charAt(0)
                                )}
                              </div>
                            ))}
                            {deletionCheckData.activeEnrollmentsCount > 5 && (
                              <span className="text-xs text-white/40 ml-1">
                                +{deletionCheckData.activeEnrollmentsCount - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Paid Enrollments Warning */}
                {deletionCheckData.hasPaidEnrollments && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <DollarSign className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-400 text-sm">Paid Enrollments</h4>
                        <p className="text-white/60 text-sm mt-1">
                          {deletionCheckData.paidEnrollmentsCount} student{deletionCheckData.paidEnrollmentsCount !== 1 ? 's' : ''} paid for this course. 
                          Refunds may be required before archiving.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Upcoming Meetings */}
                {deletionCheckData.hasUpcomingMeetings && (
                  <div className="bg-purple-500/5 border border-purple-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Calendar className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-purple-400 text-sm flex items-center gap-2">
                          Upcoming Sessions
                          <Radio className="w-3 h-3 animate-pulse" />
                        </h4>
                        <p className="text-white/60 text-sm mt-1">
                          {deletionCheckData.upcomingMeetingsCount} scheduled session{deletionCheckData.upcomingMeetingsCount !== 1 ? 's' : ''} will be cancelled.
                        </p>
                        {deletionCheckData.upcomingMeetings.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {deletionCheckData.upcomingMeetings.slice(0, 3).map(meeting => (
                              <li key={meeting.id} className="text-xs text-white/50 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-purple-400/50"></span>
                                {meeting.title} - {new Date(meeting.scheduledAt).toLocaleDateString()}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Documents */}
                {deletionCheckData.documentsCount > 0 && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-amber-400 text-sm">Course Documents</h4>
                        <p className="text-white/60 text-sm mt-1">
                          {deletionCheckData.documentsCount} document{deletionCheckData.documentsCount !== 1 ? 's are' : ' is'} attached. 
                          {deletionCheckData.canDelete && ' Documents will be preserved for enrolled students.'}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Notification Sent Success */}
                {notificationSent && (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-emerald-400 text-sm">Notifications Sent</h4>
                        <p className="text-white/60 text-sm mt-1">
                          Students have been notified and asked to unenroll. You can archive the course once all students have unenrolled.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Info box when can delete */}
              {deletionCheckData.canDelete && (
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 mb-6">
                  <p className="text-white/60 text-sm">
                    Archiving this course will:
                  </p>
                  <ul className="text-white/50 text-sm mt-2 space-y-1">
                    <li>• Hide the course from public listings</li>
                    <li>• Cancel all scheduled live sessions</li>
                    <li>• Preserve all course documents and content</li>
                  </ul>
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col gap-3">
                {/* Can Delete - Show Archive Button */}
                {deletionCheckData.canDelete && (
                  <div className="flex gap-3">
                    <button
                      onClick={closeDeleteModal}
                      className="flex-1 px-4 py-3 rounded-xl font-medium text-white/70 bg-white/5 hover:bg-white/10 transition-colors"
                      disabled={isDeleting === courseToDelete}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleDelete(false)}
                      disabled={isDeleting === courseToDelete}
                      className="flex-1 px-4 py-3 rounded-xl font-medium text-white bg-coral hover:bg-coral/90 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {isDeleting === courseToDelete ? (
                        <>
                          <InlineLoader />
                          Archiving...
                        </>
                      ) : (
                        <>
                          <Archive className="w-4 h-4" />
                          Archive Course
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Requires Support - Show Contact Support Button */}
                {deletionCheckData.requiresSupport && (
                  <div className="flex gap-3">
                    <button
                      onClick={closeDeleteModal}
                      className="flex-1 px-4 py-3 rounded-xl font-medium text-white/70 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      Cancel
                    </button>
                    <a
                      href="/help"
                      onClick={closeDeleteModal}
                      className="flex-1 px-4 py-3 rounded-xl font-medium text-white bg-purple-600 hover:bg-purple-500 transition-colors flex items-center justify-center gap-2"
                    >
                      <Headphones className="w-4 h-4" />
                      Contact Support
                    </a>
                  </div>
                )}

                {/* Can Notify Students - Show Notify Button */}
                {deletionCheckData.canNotifyStudents && !deletionCheckData.requiresSupport && (
                  <div className="flex flex-col gap-3">
                    {!notificationSent ? (
                      <>
                        <button
                          onClick={handleNotifyStudents}
                          disabled={isSendingNotification}
                          className="w-full px-4 py-3 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                        >
                          {isSendingNotification ? (
                            <>
                              <InlineLoader />
                              Sending Notifications...
                            </>
                          ) : (
                            <>
                              <Bell className="w-4 h-4" />
                              Notify Students to Unenroll
                            </>
                          )}
                        </button>
                        <p className="text-xs text-white/40 text-center">
                          Students will receive a notification asking them to unenroll from this course.
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-white/60 text-center">
                        Check back later once students have unenrolled.
                      </p>
                    )}
                    <button
                      onClick={closeDeleteModal}
                      className="w-full px-4 py-3 rounded-xl font-medium text-white/70 bg-white/5 hover:bg-white/10 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
